import { analyticsEnabled } from '../stores/privacy.js';
import mixpanel from 'mixpanel-browser';

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-T0GC4LSF6Y';

/**
 * Extended window type with Google Analytics properties
 * @typedef {Object} AnalyticsWindow
 * @property {Function} gtag - Google Analytics tag function
 * @property {Array<any>} dataLayer - Google Analytics data layer
 */

/**
 * Analytics service for tracking user interactions and e-commerce events
 * Uses Google Analytics 4 for data collection
 * @class
 */
export class AnalyticsService {
  /**
   * Creates a new analytics service instance
   * Sets up subscription to analytics consent
   * @param {Window & typeof globalThis & Partial<AnalyticsWindow>} [windowObj=window] - Window object for dependency injection in tests
   */
  constructor(windowObj = window) {
    /**
     * Window object reference
     * @type {Window & typeof globalThis & Partial<AnalyticsWindow>}
     * @private
     */
    this._window = windowObj;
    
    /**
     * Whether analytics has been initialized
     * @type {boolean}
     */
    this.initialized = false;
    
    /**
     * Subscription to analytics consent
     * @type {Function}
     * @private 
     */
    this._unsubscribe = analyticsEnabled.subscribe(enabled => {
      if (enabled && !this.initialized) {
        // this._initializeAnalytics();
        this._initializeMixpanel();
      }
    });
  }
  
  /**
   * Initializes Google Analytics if not already initialized
   * @private
   * @returns {void}
   */
  _initializeAnalytics() {
    if (this.initialized) return;
    
    try {
      // Add Google Analytics script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);
      
      // Initialize gtag
      // @ts-ignore - Safely ignore these assignments for type checking
      this._window.dataLayer = this._window.dataLayer || [];
      this._window.gtag = function() {
        // @ts-ignore
        this.dataLayer.push(arguments);
      }.bind(this._window);
      
      // @ts-ignore
      this._window.gtag('js', new Date());
      // @ts-ignore
      this._window.gtag('config', GA_MEASUREMENT_ID, {
        anonymize_ip: true,
        debug_mode: true
      });
      
      this.initialized = true;
      console.log('Analytics initialized');
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
    }
  }
  
  /**
   * Initializes Mixpanel if not already initialized
   * @private
   * @returns {void}
   */
  _initializeMixpanel() {
    mixpanel.init('28e669f9bc49fb1f04ce2495a1a6a75c', {
      debug: true,
      track_pageview: true,
      record_sessions_percent: 100,  //records 1% of all sessions
      record_block_selector: 'video',

    });
    this.initialized = true;
  }
  
  /**
   * Generic event tracking method
   * @param {string} eventName - Name of the event to track
   * @param {Object} eventParams - Parameters associated with the event
   * @returns {void}
   */
  trackEvent(eventName, eventParams = {}) {
    console.log('trackEvent: ' + eventName + ' ' + JSON.stringify(eventParams));
    if (!this.initialized) return;
    console.log('mixpanel.track: ' + eventName + ' ' + JSON.stringify(eventParams));

    
    // @ts-ignore
    // if (this._window.gtag) {
    //   // @ts-ignore
    //   this._window.gtag('event', eventName, eventParams);
    // }
    mixpanel.track(eventName, eventParams);
  }
  
  /**
   * Tracks general user actions
   * @param {string} action - Action performed by user (e.g., 'click_button', 'open_dialog')
   * @param {Object} params - Additional parameters for the action
   * @returns {void}
   */
  trackUserAction(action, params = {}) {
    const eventName = `user_${action}`;
    console.log('trackUserAction: ' + eventName + ' ' + JSON.stringify(params));
    this.trackEvent(eventName, {
      event_category: 'user_action',
      ...params
    });
  }
  
  /**
   * Tracks when a product is viewed
   * @param {import('../proto/products_pb').Product} product - The product being viewed
   * @returns {void}
   */
  trackProductView(product) {
    if (!product) return;
    console.log('trackProductView: ' + product.getId() + ' ' + product.getName());
    this.trackEvent('view_item', {
      event_category: 'ecommerce',
      items: [{
        item_id: product.getId(),
        item_name: product.getName(),
        price: product.getPrice(),
        item_category: product.getCategory(),
        discount: this.calculateDiscount(product),
        currency: 'BGN'
      }]
    });
  }

  /**
   * Tracks when a product page is changed
   * @param {number} newPage - The new page number
   * @returns {void}
   */
  trackProductPageChange(newPage) {
    this.trackEvent('product_page_change', {
      event_category: 'ecommerce',
      page: newPage
    });
  }
  /**
   * Tracks when a product is clicked
   * @param {import('../proto/products_pb').Product} product - The product being clicked
   * @param {string} [listName='product_list'] - The name of the list containing the product
   * @returns {void}
   */
  trackProductClick(product, listName = 'product_list') {
    if (!product) return;
    console.log('trackProductClick: ' + product.getId() + ' ' + product.getName());
    this.trackEvent('select_item', {
      event_category: 'ecommerce',
      item_list_name: listName,
      items: [{
        item_id: product.getId(),
        item_name: product.getName(),
        price: product.getPrice(),
        item_category: product.getCategory(),
        currency: 'BGN'
      }]
    });
  }
  
  /**
   * Tracks when a product is added to cart
   * @param {import('../proto/products_pb').Product} product - The product being added to cart
   * @param {number} [quantity=1] - Quantity of the product being added
   * @returns {void}
   */
  trackAddToCart(product, quantity = 1) {
    if (!product) return;
    console.log('trackAddToCart: ' + product.getId() + ' ' + product.getName());
    // Calculate the actual price used (discounted if available)
    const effectivePrice = this.getEffectivePrice(product);
    const discount = this.calculateDiscount(product);
    
    this.trackEvent('add_to_cart', {
      event_category: 'ecommerce',
      currency: 'BGN',
      value: effectivePrice * quantity,
      items: [{
        item_id: product.getId(),
        item_name: product.getName(),
        price: product.getPrice(),
        item_category: product.getCategory(),
        quantity: quantity,
        discount: discount
      }]
    });
  }
  
  /**
   * Tracks the beginning of checkout process
   * @param {Object} cart - Shopping cart information
   * @param {Array<import('../proto/products_pb').ShoppingCartProduct>} cart.items - Cart items
   * @param {number} [cart.totalValue] - Total cart value
   * @param {string} [deliveryArea=''] - Selected delivery area
   * @returns {void}
   */
  trackBeginCheckout(cart, deliveryArea = '') {
    if (!cart || !cart.items || !cart.items.length) return;
    const mappedItems = cart.items.map(item => {
      const product = item.getProduct();
      if (!product) return null;
      
      return {
        item_id: product.getId(),
        item_name: product.getName(),
        price: product.getPrice(),
        item_category: product.getCategory(),
        quantity: item.getQuantity(),
        discount: this.calculateDiscount(product)
      };
    });
    
    /**
     * @type {Array<{item_id: string, item_name: string, price: number, item_category: string, quantity: number, discount: number}>}
     */
    const items = mappedItems.filter(item => item !== null);
    
    if (items.length === 0) return;
    
    this.trackEvent('begin_checkout', {
      event_category: 'ecommerce',
      currency: 'BGN',
      value: cart.totalValue || this._calculateCartTotal(items),
      items: items,
      delivery_area: deliveryArea
    });
  }
  
  /**
   * Tracks a completed purchase
   * @param {Object} orderData - Order information
   * @param {string} orderData.orderId - Unique order identifier
   * @param {Array<import('../proto/products_pb').ShoppingCartProduct>} orderData.items - Items purchased
   * @param {number} orderData.totalValue - Total order value
   * @param {number} [orderData.deliveryCost=0] - Delivery cost
   * @param {string} [orderData.deliveryArea=''] - Delivery area
   * @returns {void}
   */
  trackPurchase(orderData) {
    if (!orderData || !orderData.orderId || !orderData.items || !orderData.items.length) return;
    console.log('trackPurchase: ' + orderData.orderId + ' ' + JSON.stringify(orderData.items));
    const items = orderData.items.map(item => {
      const product = item.getProduct();
      if (!product) return null;
      
      return {
        item_id: product.getId(),
        item_name: product.getName(),
        price: product.getPrice(),
        item_category: product.getCategory(),
        quantity: item.getQuantity(),
        discount: this.calculateDiscount(product)
      };
    }).filter(Boolean);
    
    if (items.length === 0) return;
    
    this.trackEvent('purchase', {
      event_category: 'ecommerce',
      transaction_id: orderData.orderId,
      value: orderData.totalValue,
      currency: 'BGN',
      tax: 0,
      shipping: orderData.deliveryCost || 0,
      items: items,
      delivery_area: orderData.deliveryArea || ''
    });
  }
  
  /**
   * Tracks form submission events
   * @param {string} formName - Name/identifier of the form
   * @param {boolean} success - Whether submission was successful
   * @param {string|null} errorDetails - Error details if submission failed
   * @returns {void}
   */
  trackFormSubmit(formName, success = true, errorDetails = null) {
    console.log('trackFormSubmit: ' + formName + ' ' + success + ' ' + errorDetails);
    this.trackEvent('form_submit', {
      event_category: 'form',
      form_name: formName,
      success: success,
      error_details: errorDetails
    });
  }
  
  /**
   * Tracks performance metrics
   * @param {string} metric - Name of the performance metric
   * @param {number} value - Value of the metric
   * @returns {void}
   */
  trackPerformance(metric, value) {
    console.log('trackPerformance: ' + metric + ' ' + value);
    this.trackEvent('performance', {
      event_category: 'performance',
      metric: metric,
      value: value
    });
  }
  
  /**
   * Tracks page views
   * @param {string} pagePath - Path of the page being viewed
   * @param {string} pageTitle - Title of the page being viewed
   * @returns {void}
   */
  trackPageView(pagePath, pageTitle) {
    console.log('trackPageView: ' + pagePath + ' ' + pageTitle);
    this.trackEvent('page_view', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
  
  /**
   * Tracks category page views
   * @param {string} category - Category being viewed
   * @returns {void}
   */
  trackCategoryView(category) {
    console.log('trackCategoryView: ' + category);
    this.trackEvent('view_item_list', {
      event_category: 'ecommerce',
      item_list_name: category
    });
  }
  
  /**
   * Tracks search actions
   * @param {string} searchTerm - The search term entered by the user
   * @param {number} resultCount - Number of results returned
   * @returns {void}
   */
  trackSearch(searchTerm, resultCount) {
    console.log('trackSearch: ' + searchTerm + ' ' + resultCount);
    this.trackEvent('search', {
      search_term: searchTerm,
      result_count: resultCount
    });
  }
  
  /**
   * Tracks removal of item from cart
   * @param {import('../proto/products_pb').Product} product - The product being removed
   * @param {number} [quantity=1] - Quantity being removed
   * @returns {void}
   */
  trackRemoveFromCart(product, quantity = 1) {
    if (!product) return;
    console.log('trackRemoveFromCart: ' + product.getId() + ' ' + product.getName());
    this.trackEvent('remove_from_cart', {
      event_category: 'ecommerce',
      items: [{
        item_id: product.getId(),
        item_name: product.getName(),
        price: product.getPrice(),
        quantity: quantity
      }]
    });
  }
  
  /**
   * Calculate discount amount for a product
   * @param {import('../proto/products_pb').Product} product - The product to calculate discount for
   * @returns {number} - The discount amount (0 if no discount)
   */
  calculateDiscount(product) {
    if (!product) return 0;
    
    const price = product.getPrice();
    const discountedPrice = product.getDiscountedprice();
    
    return (discountedPrice && discountedPrice !== price) 
      ? Number((price - discountedPrice).toFixed(2))
      : 0;
  }
  
  /**
   * Get the effective price for a product (discounted or regular)
   * @param {import('../proto/products_pb').Product} product - The product to get price for
   * @returns {number} - The effective price
   */
  getEffectivePrice(product) {
    if (!product) return 0;
    
    const price = product.getPrice();
    const discountedPrice = product.getDiscountedprice();
    
    return (discountedPrice && discountedPrice !== price) 
      ? discountedPrice 
      : price;
  }
  
  /**
   * Calculate total value of items in cart
   * @private
   * @param {Array<{price: number, quantity: number}>} items - Processed cart items with price and quantity
   * @returns {number} - Total cart value
   */
  _calculateCartTotal(items) {
    return items.reduce((sum, item) => {
      const price = item.price || 0;
      const quantity = item.quantity || 1;
      return sum + (price * quantity);
    }, 0);
  }
  
  /**
   * Cleans up subscriptions when service is destroyed
   * @returns {void}
   */
  destroy() {
    if (this._unsubscribe) {
      this._unsubscribe();
    }
  }
  
  /**
   * For testing only: Force initialization state
   * @param {boolean} value - Initialization state to set
   */
  _setInitialized(value) {
    this.initialized = value;
  }
}

const analytics = new AnalyticsService();
export default analytics; 